import React from 'react';
import FilterBar from '../FilterBar';
import GenerateForm from '../ImageGeneration/GenerateForm';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { ImageType } from '../FilterBar/TypeFilter';
import { ImageDown } from 'lucide-react';
import SidebarHeader from '@/components/ui/SidebarHeader';
import MobileSidebarAccordion from '@/components/ui/MobileSidebarAccordion';
import { useIsMobile } from '@/hooks/layout/use-mobile';

interface FilterSectionProps {
  selectedType: ImageType | null;
  onTypeChange: (type: ImageType) => void;
  styleOptions: FilterOption[];
  selectedStyle: string;
  onStyleChange: (options: FilterOption[]) => void;
  setSelectedStyle: (style: string | null) => void;
  influenceOptions: FilterOption[];
  selectedInfluence: string;
  onInfluenceChange: (options: FilterOption[]) => void;
  setSelectedInfluence: (influence: string | null) => void;
  colorOptions: FilterOption[];
  onColorChange: (options: FilterOption[]) => void;
  backgroundOptions: FilterOption[];
  onBackgroundChange: (options: FilterOption[]) => void;
  selectedBackground: string;
  environmentOptions: FilterOption[];
  onEnvironmentChange: (options: FilterOption[]) => void;
  moodOptions: FilterOption[];
  onMoodChange: (options: FilterOption[]) => void;
  imageDescription: string;
  onImageDescriptionChange: (value: string) => void;
  width: string;
  height: string;
  onWidthChange: (value: string | string[]) => void;
  onHeightChange: (value: string | string[]) => void;
  isGenerating: boolean;
  isImageLoading: boolean;
  onGenerate: () => void;
  isAdvancedMode: boolean;
  setIsAdvancedMode: (value: boolean) => void;
  selectedProjectId?: string | null;
  onProjectChange?: (projectId: string | null) => void;
}

const FilterSection: React.FC<FilterSectionProps> = React.memo(({
  selectedType,
  onTypeChange,
  styleOptions,
  selectedStyle,
  onStyleChange,
  setSelectedStyle,
  influenceOptions,
  selectedInfluence,
  onInfluenceChange,
  setSelectedInfluence,
  colorOptions,
  onColorChange,
  backgroundOptions,
  onBackgroundChange,
  selectedBackground,
  environmentOptions,
  onEnvironmentChange,
  moodOptions,
  onMoodChange,
  imageDescription,
  onImageDescriptionChange,
  width,
  height,
  onWidthChange,
  onHeightChange,
  isGenerating,
  isImageLoading,
  onGenerate,
  isAdvancedMode,
  setIsAdvancedMode,
  selectedProjectId,
  onProjectChange
}) => {
  const isMobile = useIsMobile();
  
  return (
    <MobileSidebarAccordion 
      variant="purple" 
      persistKey="image-generator-sidebar"
      defaultExpanded={false}
      className="h-full"
    >
      <SidebarHeader title="Create Image" icon={ImageDown} variant="purple" />
      
      {/* Each component must be a direct child of MobileSidebarAccordion */}
      <FilterBar 
        selectedType={selectedType}
        onTypeChange={onTypeChange}
        styleOptions={styleOptions}
        selectedStyle={selectedStyle}
        onStyleChange={onStyleChange}
        setSelectedStyle={setSelectedStyle}
        influenceOptions={influenceOptions}
        selectedInfluence={selectedInfluence}
        onInfluenceChange={onInfluenceChange}
        setSelectedInfluence={setSelectedInfluence}
        colorOptions={colorOptions}
        onColorChange={onColorChange}
        backgroundOptions={backgroundOptions}
        onBackgroundChange={onBackgroundChange}
        selectedBackground={selectedBackground}
        environmentOptions={environmentOptions}
        onEnvironmentChange={onEnvironmentChange}
        moodOptions={moodOptions}
        onMoodChange={onMoodChange}
        imageDescription={imageDescription}
        onImageDescriptionChange={onImageDescriptionChange}
        width={width}
        height={height}
        onWidthChange={onWidthChange}
        onHeightChange={onHeightChange}
        isAdvancedMode={isAdvancedMode}
        setIsAdvancedMode={setIsAdvancedMode}
        selectedProjectId={selectedProjectId}
        onProjectChange={onProjectChange}
      />
      
      <GenerateForm
        imageDescription={imageDescription}
        isGenerating={isGenerating}
        isImageLoading={isImageLoading}
        selectedType={selectedType}
        styleOptions={styleOptions}
        selectedStyle={selectedStyle}
        influenceOptions={influenceOptions}
        selectedInfluence={selectedInfluence}
        colorOptions={colorOptions}
        backgroundOptions={backgroundOptions}
        selectedBackground={selectedBackground}
        environmentOptions={environmentOptions}
        moodOptions={moodOptions}
        width={width}
        height={height}
        onGenerate={onGenerate}
      />
      
      {/* License link removed as requested */}
    </MobileSidebarAccordion>
  );
});

FilterSection.displayName = 'FilterSection';

export default FilterSection;
